// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <div>
    <v-navigation-drawer app right clipped persistent v-model="showDrawer" mobile-breakpoint="800">
      <v-container grid-list-xs>
        <v-row wrap dense>
          <v-col cols="12">
            <v-btn icon @click="showDrawer = false">
              <v-icon>clear</v-icon>
            </v-btn>
            <span class="title">Metadata</span>
          </v-col>
          <v-col cols="12" v-if="canModify">
            <v-btn block color="success" dark @click="save">Save<v-icon right dark>create</v-icon></v-btn>
          </v-col>
          <v-col cols="12">
            <v-text-field
            :value="formatTime(properties.timestamp)"
            prepend-icon="watch_later"
            hide-details
            readonly>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
            v-if="userInfo.username"
            v-model="userInfo.username.toLowerCase()"
            hide-details
            label="Uploader"
            prepend-icon="account_circle"
            readonly>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
            v-if="properties.location"
            v-model="properties.location.coordinates[1]"
            @click:prepend="openMaps(location)"
            hide-details
            label="Latitude"
            prepend-icon="gps_fixed"
            readonly>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
            v-if="properties.location"
            v-model="properties.location.coordinates[0]"
            @click:prepend="openMaps(location)"
            hide-details
            label="Longitude"
            prepend-icon="gps_fixed"
            readonly>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
            :items="getCategories"
            v-model="properties.categories"
            item-text="name"
            item-value="id"
            label="Categories"
            return-object
            hide-details
            chips deletable-chips multiple hide-selected>
          </v-autocomplete>
          </v-col>
          <v-col cols="12">
            <v-combobox multiple
            label="Tags"
            v-model="properties.tags"
            :items="getTags"
            chips
            deletable-chips
            hide-details
            style="margin-top:15px;">
            </v-combobox>
          </v-col>
          <v-col cols="12">
            <v-textarea
            v-model="properties.annotation"
            auto-grow
            hide-details
            label="Comment"
            outline
            style="margin-top: 8px; padding-top: 8px;"
            ></v-textarea>
          </v-col>
          <v-list dense>
            <v-list-group :value="false">
              <v-list-item slot="activator" class="meta">
                <v-list-item-content>
                  <v-list-item-title style="color:gray;">More Information</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-col cols="12"  v-for="item in infoArray" :key="item.title">
                <v-text-field dense
                v-if="item.title"
                v-model="item.title"
                hide-details
                class="more-info"
                :label="item.value.charAt(0).toUpperCase() + item.value.slice(1)"
                readonly>
                </v-text-field>
              </v-col>
            </v-list-group>
          </v-list>
        </v-row>
      </v-container>
    </v-navigation-drawer>

    <v-container fluid grid-list-md>
      <v-card>
        <div>
        <v-toolbar dense flat >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon @click="handleNav('exit')" v-on="on">
                <v-icon>arrow_back</v-icon>
              </v-btn>
            </template>
            <span>Exit</span>
          </v-tooltip>
          <v-spacer></v-spacer>

          <v-tooltip bottom v-if="canDelete">
            <template v-slot:activator="{ on }" >
              <v-btn @click="showDeleteDialog = true" icon v-on="on">
                <v-icon dark>delete</v-icon>
              </v-btn>
            </template>
            <span>Delete</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn download :href="downloadLink" icon v-on="on">
                <v-icon>get_app</v-icon>
              </v-btn>
            </template>
            <span>Download</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon text @click.native.stop="showDrawer = !showDrawer" v-on="on">
                <v-icon>info</v-icon>
              </v-btn>
            </template>
            <span>Show Metadata</span>
          </v-tooltip>
        </v-toolbar>
        </div>
        <div id="lrg-image">
          <div id="right-button-area" v-if="canNavRight" @click="handleNav('right')">
            <div id="right-button">
              <v-btn fab :style="`background-color: ${navColor};`"><v-icon>keyboard_arrow_right</v-icon></v-btn>
            </div>
          </div>
          <div id="left-button-area" v-if="canNavLeft" @click="handleNav('left')">
            <div id="left-button">
              <v-btn fab :style="`background-color: ${navColor};`"><v-icon>keyboard_arrow_left</v-icon></v-btn>
            </div>
          </div>
          <img id='main-img' ref="image" v-bind:src="imageUrl" alt="Loading..." @load="handleImageLoad">
        </div>
      </v-card>
    </v-container>
    <error-snackbar v-model="errorText"></error-snackbar>

    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <div class="headline">Delete?</div>
        </v-card-title>
        <v-card-text>Are you sure you want to delete this image?  This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click.native="showDeleteDialog = false">Cancel</v-btn>
          <v-btn flat color="red" @click.native="deleteFile">I'm Sure</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showChangesDialog" max-width="400px">
      <v-card>
        <v-card-title class="unsaved-text" style="font-size: 1em; text-align: center; white-space:pre-line; word-break:break-word;">
          <p>Unsaved Changes Exist</p>
        </v-card-title>
        <v-card-subtitle><p>Would you like to <b>discard</b> or <b>save</b> those changes?</p></v-card-subtitle>
        <v-divider></v-divider>
        <v-card-text style="white-space:pre-wrap;"></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="handleNav('discard')">Discard</v-btn>
          <v-btn color="success" @click="save">Save<v-icon right dark>create</v-icon></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import {checkPermission} from 'vue-common/components/user'
  import {ENDPOINT} from '@/shared/constants'
  import {ImageRotatorMixin} from '../shared/mixins'
  import ErrorSnackbar from 'vue-common/components/ErrorSnackbar'
  import { mapActions, mapGetters } from 'vuex'
  import {PERMISSIONS} from 'vue-common/components/constants'
  import ApiRequests from '@/api/ApiRequests'
  import EXIF from 'exif-js'

  export default {
    components: {
      ErrorSnackbar
    },

    mixins: [
      ImageRotatorMixin
    ],

    data () {
      return {
        showDrawer: false,
        endpoint: ENDPOINT,
        errorText: '',
        initialProperties: {},
        properties: {},
        userInfo: {},
        deviceInfo: {},
        navPath: '',
        infoArray: [],
        showChangesDialog: false,
        showDeleteDialog: false,
        width: 0,
        imageLoaded: false,
        propertiesLoaded: false,
        isEditing: true
      }
    },

    computed: {
      ...mapGetters([
        'accessRules',
        'getCategories',
        'getImages',
        'getQuery',
        'getTags'
      ]),

      navColor: function () {
        if (this.$vuetify.theme.isDark) {
          return 'rgba(0,0,0,0.5)'
        } else {
          return 'rgba(255,255,255,0.4)'
        }
      },

      canDelete: function () {
        return checkPermission(this.accessRules, 'images', PERMISSIONS.DELETE)
      },

      canModify: function () {
        return checkPermission(this.accessRules, 'images', PERMISSIONS.MODIFY)
      },

      canNavLeft: function () {
        return this.imageIndex > 0
      },

      canNavRight: function () {
        return (this.imageIndex + 1) < this.getImages.length
      },

      downloadLink: function () {
        return 'api/images/' + this.properties.id
      },

      imageIndex: function () {
        return this.getImages.findIndex(e => e.id === this.$route.params.id)
      },

      imageUrl: function () {
        if (this.isMobile) {
          return this.endpoint + 'images/' + this.$route.params.id + '/thumb'
        }
        return this.endpoint + 'images/' + this.$route.params.id
      },

      isMobile: function () {
        return this.$vuetify.breakpoint.xsOnly
      },

      location: function () {
        if (this.properties.location && this.properties.location.coordinates) {
          return this.properties.location.coordinates[1] + ', ' +
            this.properties.location.coordinates[0]
        } else {
          return '0, 0'
        }
      }
    },

    beforeMount () {
      window.addEventListener('beforeunload', this.checkEditing)
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('beforeunload', this.checkEditing)
      })
    },

    beforeRouteLeave (to, from, next) {
      if (this.initialProperties.annotation !== this.properties.annotation || this.initialProperties.tags !== this.properties.tags || this.initialProperties.categories !== this.properties.categories) {
        this.navPath = to.path
        this.showChangesDialog = true
      } else {
        next()
      }
    },

    beforeRouteUpdate (to, from, next) {
      if (this.initialProperties.annotation !== this.properties.annotation || this.initialProperties.tags !== this.properties.tags || this.initialProperties.categories !== this.properties.categories) {
        this.navPath = to.path
        this.showChangesDialog = true
      } else {
        next()
      }
    },

    created () {
      this.getProperties(this.$route.params.id)
      if (this.getImages.length < 1) {
        this.fetchImages()
      }
      this.checkLoadMore()
      document.addEventListener('keyup', this.keyListener)
      this.showDrawer = !this.isMobile
    },

    destroyed () {
      document.removeEventListener('keyup', this.keyListener)
    },

    watch: {
      $route: function (to, from) {
        this.handleImageChange()
        this.getProperties(to.params.id)
        this.checkLoadMore()
      },

      'properties.annotation' (newVal, oldVal) {
        if (this.properties.annotation !== this.initialProperties.annotation) {
          this.isEditing = true
          // console.log(this.isEditing)
        } else {
          this.isEditing = false
          // console.log(this.isEditing)
        }
      },

      'properties.tags' (newVal, oldVal) {
        if (this.properties.tags !== this.initialProperties.tags) {
          this.isEditing = true
          // console.log(this.isEditing)
        } else {
          this.isEditing = false
          // console.log(this.isEditing)
        }
      },

      'properties.categories' (newVal, oldVal) {
        if (this.properties.categories !== this.initialProperties.categories) {
          this.isEditing = true
          // console.log(this.isEditing)
        } else {
          this.isEditing = false
          // console.log(this.isEditing)
        }
      }
    },

    methods: {
      ...mapActions([
        'fetchImages',
        'incrementLimit'
      ]),

      getUsername: function () {
        this.userInfo.username = ' '
        ApiRequests.getUsername(this.properties.uploader)
          .then((response) => {
            this.userInfo = response.data
          })
          .catch(() => {
            console.error('Uploader username not found')
            this.userInfo.username = 'unavailable'
          })
      },

      getProperties: function (id) {
        ApiRequests.getImageProperties(id)
          .then((response) => {
            this.properties = response.data
            this.$data.propertiesLoaded = true
            if (this.$data.imageLoaded) {
              // this.rotateImage(this.$refs['image'], (this.properties.exif_data && this.properties.exif_data.orientation ? this.properties.exif_data.orientation.value : 1))
            }

            if (this.$route.query.fromUpload) {
              // Pre-fill properties.
              var recentEntries = getRecentEntries()
              for (var i in recentEntries) {
                if (this.properties[i] !== undefined) {
                  if ((typeof recentEntries[i]) === 'object') {
                    this.properties[i] = recentEntries[i]
                  }
                }
              }
            }
            this.setInitialProperties()
            this.getUsername()
          })
          .catch((e) => {
            console.error('error loading image properties: %o', e)
          })
          .finally(() => {
            this.sortDeviceInfo()
          })
      },

      getExifPromise: function () {
        return new Promise((resolve) => {
          const img = document.getElementById('main-img')
          EXIF.getData(img, () => {
            var make = EXIF.getTag(img, 'Make')
            var model = EXIF.getTag(img, 'Model')
            if (make && model) {
              resolve(`${EXIF.getTag(img, 'Make')} ${EXIF.getTag(img, 'Model')}`)
            } else {
              resolve(undefined)
            }
          })
        })
      },

      sortDeviceInfo: async function () {
        this.deviceInfo.size = this.formatBytes(this.properties.cloud_photos[0].bytes)
        this.deviceInfo.resolution = `${this.properties.cloud_photos[0].size.Y} x ${this.properties.cloud_photos[0].size.X}`
        this.deviceInfo.format = this.properties.cloud_photos[0].format.toUpperCase()
        this.deviceInfo.device = await this.getExifPromise()
        this.populateInfo()
      },

      formatBytes: function (bytes) {
        const size = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return parseFloat((bytes / Math.pow(1000, i)).toFixed(2)) + ' ' + size[i]
      },

      populateInfo: function () {
        this.infoArray = []
        for (let x in this.deviceInfo) {
          const obj = {title: this.deviceInfo[x], value: x}
          this.infoArray.push(obj)
        }
      },

      checkLoadMore: function () {
        if (this.imageIndex >= (this.getImages.length - 1)) {
          this.incrementLimit()
        }
      },

      handleNav: function (action) {
        switch (action) {
          case 'exit':
            this.$router.push({name: 'images'})
            break
          case 'right':
            if (this.canNavRight) {
              var id = this.getImages[this.imageIndex + 1].id
              this.$router.push({name: 'imageEdit', params: {id: id}})
            }
            break
          case 'left':
            if (this.canNavLeft) {
              id = this.getImages[this.imageIndex - 1].id
              this.$router.push({name: 'imageEdit', params: {id: id}})
            }
            break
          case 'discard':
            this.restoreInitialProperties()
            this.showChangesDialog = false
            this.$router.push({path: `${this.navPath}`})
            break
        }
      },

      checkEditing: function (event) {
        if (!this.isEditing) return
        event.preventDefault()
        event.returnValue = ''
      },

      setInitialProperties: function () {
        for (const x in this.properties) {
          this.initialProperties[x] = this.properties[x]
        }
      },

      restoreInitialProperties: function () {
        for (const x in this.initialProperties) {
          this.properties[x] = this.initialProperties[x]
        }
      },

      deleteFile: function () {
        ApiRequests.deleteImage(this.$route.params.id).then(response => {
          this.$router.go(-1)
        })
        .catch(e => {
          console.log(e)
        })
      },

      formatTime: function (value) {
        if (value) {
          if (value !== '0001-01-01T00:00:00Z') {
            return moment(value).format('MM/DD/YYYY hh:mm A')
          }
        }
        return ''
      },

      handleImageChange: function () {
        delete this.$refs['image'].dataset.rotated
        this.$data.imageLoaded = false
        this.$data.propertiesLoaded = false
      },

      handleImageLoad: function () {
        this.$data.imageLoaded = true
        this.sortDeviceInfo()
        if (this.$data.propertiesLoaded) {
          // this.rotateImage(this.$refs['image'], (this.properties.exif_data && this.properties.exif_data.orientation ? this.properties.exif_data.orientation.value : 1))
        }
      },

      keyListener: function (event) {
        if (event.keyCode === 37) {
          this.handleNav('left')
        } else if (event.keyCode === 39) {
          this.handleNav('right')
        }
      },

      openMaps: function (location) {
        window.open('https://www.google.com/maps/search/?api=1&query=' + location)
      },

      save: function () {
        ApiRequests.putImageProperties(this.$route.params.id, this.properties)
          .then((response) => {
            this.properties = response.data
            this.errorText = 'Saved'
            if (this.$route.query.fromUpload) {
              // Store properties for future pre-filling.
              setRecentEntries(this.properties)
            }
          })
          .catch((e) => {
            console.error('error saving image properties: %o', e)
          })
          .finally(() => {
            this.setInitialProperties()
            if (this.showChangesDialog === true) {
              this.handleNav('discard')
            }
          })
      }
    }
  }

  const STORAGE = 'edit_storage'

  function getRecentEntries () {
    return JSON.parse(localStorage.getItem(STORAGE))
  }

  function setRecentEntries (properties) {
    var value = {}
    for (var i in properties) {
      if (i === 'categories' || i === 'tags') {
        value[i] = properties[i]
      }
    }
    localStorage.setItem(STORAGE, JSON.stringify(value))
  }

</script>

<style scoped>

  .rotated {
    image-orientation: from-image;
  }

  a {
    text-decoration: none;
  }

  #lrg-image {
    width: 100%;
  }

  #lrg-image img {
    display: block;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 5%;
    height: auto;
    width: auto;
    max-height: 100%;
    max-width: 100%;
    user-select: none;
  }

  #right-button-area {
    position: absolute;
    right: 0;
    padding-right: 10px;
    width: 80px;
    height: 100%;
    text-align: right;
    cursor: pointer;
  }
  #left-button-area {
    position: absolute;
    left: 0;
    padding-left: 10px;
    width: 80px;;
    height: 100%;
    cursor: pointer;
  }

  #right-button {
    position: absolute;
    top: 50%;
    right: 0;
    margin-right: 10px;
  }
  #left-button {
    position: absolute;
    top: 50%;
  }

</style>



// WEBPACK FOOTER //
// Image.vue?81e18c00